import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import AnamnesisTemplateBuilderFields from "../../../../Components/Studio/AnamnesisTemplateBuilderFields";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { entityFormKey } from "../../../../helpers/entityEditForm";
import {
  BuilderQuestion,
  questionsToSchema,
  schemaToQuestions,
} from "../../Studio/Settings/AnamnesisTemplates";
import {
  createHealthRecordPreset,
  deleteHealthRecordPreset,
  getHealthRecordPreset,
  getHealthRecordPresets,
  HealthRecordTemplatePreset,
  updateHealthRecordPreset,
} from "../../../../services/healthRecordPresetService";

const PER_PAGE = 50;

type PresetFormValues = {
  slug: string;
  title: string;
  specialty: string;
  record_type: "anamnesis" | "evolution";
  sort_order: number;
  questions: BuilderQuestion[];
  is_active: boolean;
};

const emptyFormValues = (): PresetFormValues => ({
  slug: "",
  title: "",
  specialty: "capilar",
  record_type: "anamnesis",
  sort_order: 0,
  questions: [],
  is_active: true,
});

const mapPresetToFormValues = (preset: HealthRecordTemplatePreset): PresetFormValues => ({
  slug: preset.slug,
  title: preset.title,
  specialty: preset.specialty,
  record_type: preset.record_type === "evolution" ? "evolution" : "anamnesis",
  sort_order: preset.sort_order,
  questions: schemaToQuestions(preset.schema_json),
  is_active: preset.is_active,
});

const SuperAdminHealthRecordPresets = () => {
  const { t } = useTranslation();

  document.title = t("admin.health_record_presets.pageTitle");

  const [presets, setPresets] = useState<HealthRecordTemplatePreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState<HealthRecordTemplatePreset | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<HealthRecordTemplatePreset | null>(null);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        slug: Yup.string()
          .required(t("admin.health_record_presets.validation.slugRequired"))
          .max(64, t("admin.health_record_presets.validation.slugMax")),
        title: Yup.string().required(
          t("admin.health_record_presets.validation.titleRequired")
        ),
        specialty: Yup.string().required(
          t("admin.health_record_presets.validation.specialtyRequired")
        ),
        sort_order: Yup.number()
          .min(0)
          .max(65535)
          .required(t("admin.health_record_presets.validation.sortOrderRequired")),
        questions: Yup.array()
          .min(1, t("studio.anamnesis.builder.validation.questionsMin"))
          .of(
            Yup.object({
              label: Yup.string().required(
                t("studio.anamnesis.builder.validation.labelRequired")
              ),
              type: Yup.string().required(
                t("studio.anamnesis.builder.validation.typeRequired")
              ),
              options: Yup.string().when("type", {
                is: "select",
                then: (schema) =>
                  schema.required(t("studio.anamnesis.builder.validation.optionsRequired")),
              }),
            })
          ),
        is_active: Yup.boolean(),
      }),
    [t]
  );

  const loadPresets = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getHealthRecordPresets({ per_page: PER_PAGE });
      setPresets(response.data ?? []);
    } catch (error: any) {
      toast.error(error?.data || t("admin.health_record_presets.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  const formInitialValues = useMemo(
    () => (editingPreset ? mapPresetToFormValues(editingPreset) : emptyFormValues()),
    [editingPreset]
  );

  const formik = useFormik<PresetFormValues>({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, helpers) => {
      setSubmitting(true);

      const payload = {
        slug: values.slug.trim(),
        title: values.title.trim(),
        specialty: values.specialty.trim(),
        record_type: values.record_type,
        sort_order: values.sort_order,
        schema_json: questionsToSchema(values.questions),
        is_active: values.is_active,
      };

      try {
        if (editingPreset) {
          await updateHealthRecordPreset(editingPreset.id, payload);
          toast.success(t("admin.health_record_presets.toast.updateSuccess"));
        } else {
          await createHealthRecordPreset(payload);
          toast.success(t("admin.health_record_presets.toast.createSuccess"));
        }

        setModalOpen(false);
        setEditingPreset(null);
        await loadPresets();
      } catch (error: any) {
        toast.error(error?.data || t("admin.health_record_presets.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const openCreate = () => {
    setEditingPreset(null);
    setFormLoading(false);
    setModalOpen(true);
  };

  const openEdit = async (preset: HealthRecordTemplatePreset) => {
    setEditingPreset(null);
    setFormLoading(true);
    setModalOpen(true);

    try {
      const response = await getHealthRecordPreset(preset.id);
      setEditingPreset(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("admin.health_record_presets.toast.loadError"));
      closeModal();
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingPreset(null);
    setFormLoading(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      await deleteHealthRecordPreset(deleteTarget.id);
      toast.success(t("admin.health_record_presets.toast.deleteSuccess"));
      setDeleteModal(false);
      setDeleteTarget(null);
      await loadPresets();
    } catch (error: any) {
      toast.error(error?.data || t("admin.health_record_presets.toast.deleteError"));
    }
  };

  const getQuestionError = (index: number, field: keyof BuilderQuestion): string | undefined => {
    const questionErrors = formik.errors.questions;

    if (!Array.isArray(questionErrors)) {
      return undefined;
    }

    const questionError = questionErrors[index];

    if (!questionError || typeof questionError === "string") {
      return undefined;
    }

    const fieldError = questionError[field];

    return typeof fieldError === "string" ? fieldError : undefined;
  };

  const specialtyLabel = (specialty: string): string =>
    t(`admin.health_record_presets.specialty.${specialty}`, specialty);

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("admin.health_record_presets.breadcrumb.title")}
            pageTitle={t("admin.health_record_presets.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <h4 className="card-title mb-0 flex-grow-1">
                    {t("admin.health_record_presets.card.title")}
                  </h4>
                  <Button color="primary" onClick={openCreate}>
                    <i className="ri-add-line align-bottom me-1" />
                    {t("admin.health_record_presets.actions.new")}
                  </Button>
                </CardHeader>
                <CardBody>
                  <p className="text-muted mb-4">
                    {t("admin.health_record_presets.card.description")}
                  </p>

                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : presets.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-3">{t("admin.health_record_presets.empty.noData")}</p>
                      <Button color="soft-primary" onClick={openCreate}>
                        {t("admin.health_record_presets.actions.createFirst")}
                      </Button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("admin.health_record_presets.table.title")}</th>
                            <th>{t("admin.health_record_presets.table.specialty")}</th>
                            <th>{t("admin.health_record_presets.table.recordType")}</th>
                            <th>{t("admin.health_record_presets.table.sortOrder")}</th>
                            <th>{t("admin.health_record_presets.table.copies")}</th>
                            <th>{t("admin.health_record_presets.table.status")}</th>
                            <th className="text-end">{t("admin.health_record_presets.table.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {presets.map((preset) => (
                            <tr key={preset.id}>
                              <td>
                                <div className="fw-medium">{preset.title}</div>
                                <div className="text-muted small">{preset.slug}</div>
                              </td>
                              <td>{specialtyLabel(preset.specialty)}</td>
                              <td>
                                <Badge
                                  color={
                                    preset.record_type === "evolution"
                                      ? "info"
                                      : "secondary"
                                  }
                                >
                                  {t(
                                    `admin.health_record_presets.recordType.${
                                      preset.record_type === "evolution"
                                        ? "evolution"
                                        : "anamnesis"
                                    }`
                                  )}
                                </Badge>
                              </td>
                              <td>{preset.sort_order}</td>
                              <td>{preset.tenant_templates_count ?? 0}</td>
                              <td>
                                <Badge color={preset.is_active ? "success" : "secondary"}>
                                  {preset.is_active
                                    ? t("admin.health_record_presets.status.active")
                                    : t("admin.health_record_presets.status.inactive")}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => openEdit(preset)}
                                >
                                  <i className="ri-pencil-line" />
                                </Button>
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  onClick={() => {
                                    setDeleteTarget(preset);
                                    setDeleteModal(true);
                                  }}
                                >
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={closeModal} centered size="lg">
        <ModalHeader toggle={closeModal}>
          {editingPreset
            ? t("admin.health_record_presets.form.editTitle")
            : t("admin.health_record_presets.form.createTitle")}
        </ModalHeader>
        <ModalBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("clients.loading")}</Spinner>
            </div>
          ) : (
          <FormikProvider value={formik}>
            <Form key={entityFormKey(editingPreset?.id)} onSubmit={formik.handleSubmit}>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="preset-title">
                      {t("admin.health_record_presets.fields.title.label")}
                    </Label>
                    <Input
                      id="preset-title"
                      name="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      invalid={!!(formik.touched.title && formik.errors.title)}
                    />
                    {formik.touched.title && formik.errors.title ? (
                      <FormFeedback>{formik.errors.title}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="preset-slug">
                      {t("admin.health_record_presets.fields.slug.label")}
                    </Label>
                    <Input
                      id="preset-slug"
                      name="slug"
                      value={formik.values.slug}
                      onChange={formik.handleChange}
                      disabled={Boolean(editingPreset)}
                      invalid={!!(formik.touched.slug && formik.errors.slug)}
                    />
                    {formik.touched.slug && formik.errors.slug ? (
                      <FormFeedback>{formik.errors.slug}</FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label htmlFor="preset-specialty">
                      {t("admin.health_record_presets.fields.specialty.label")}
                    </Label>
                    <Input
                      id="preset-specialty"
                      name="specialty"
                      type="select"
                      value={formik.values.specialty}
                      onChange={formik.handleChange}
                    >
                      <option value="capilar">
                        {t("admin.health_record_presets.specialty.capilar")}
                      </option>
                      <option value="facial">
                        {t("admin.health_record_presets.specialty.facial")}
                      </option>
                      <option value="corporal">
                        {t("admin.health_record_presets.specialty.corporal")}
                      </option>
                      <option value="geral">
                        {t("admin.health_record_presets.specialty.geral")}
                      </option>
                      <option value="protocolo">
                        {t("admin.health_record_presets.specialty.protocolo")}
                      </option>
                    </Input>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label htmlFor="preset-record-type">
                      {t("admin.health_record_presets.fields.recordType.label")}
                    </Label>
                    <Input
                      id="preset-record-type"
                      name="record_type"
                      type="select"
                      value={formik.values.record_type}
                      onChange={formik.handleChange}
                    >
                      <option value="anamnesis">
                        {t("admin.health_record_presets.recordType.anamnesis")}
                      </option>
                      <option value="evolution">
                        {t("admin.health_record_presets.recordType.evolution")}
                      </option>
                    </Input>
                    <p className="text-muted small mb-0 mt-1">
                      {t("admin.health_record_presets.fields.recordType.help")}
                    </p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label htmlFor="preset-sort-order">
                      {t("admin.health_record_presets.fields.sortOrder.label")}
                    </Label>
                    <Input
                      id="preset-sort-order"
                      name="sort_order"
                      type="number"
                      min={0}
                      value={formik.values.sort_order}
                      onChange={formik.handleChange}
                    />
                  </div>
                </Col>
              </Row>

              <AnamnesisTemplateBuilderFields getQuestionError={getQuestionError} />

              <div className="form-check form-switch mb-4">
                <Input
                  type="checkbox"
                  role="switch"
                  className="form-check-input"
                  id="preset-active"
                  name="is_active"
                  checked={formik.values.is_active}
                  onChange={formik.handleChange}
                />
                <Label className="form-check-label" htmlFor="preset-active">
                  {t("admin.health_record_presets.fields.isActive")}
                </Label>
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <Button color="light" type="button" onClick={closeModal}>
                  {t("admin.health_record_presets.actions.cancel")}
                </Button>
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? <Spinner size="sm" className="me-1" /> : null}
                  {t("admin.health_record_presets.actions.save")}
                </Button>
              </div>
            </Form>
          </FormikProvider>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          {t("admin.health_record_presets.delete.title")}
        </ModalHeader>
        <ModalBody>
          <p>{t("admin.health_record_presets.delete.description", { title: deleteTarget?.title })}</p>
          <div className="d-flex gap-2 justify-content-end">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("admin.health_record_presets.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete}>
              {t("admin.health_record_presets.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default SuperAdminHealthRecordPresets;
